import { useCallback, useMemo } from 'react';

import { corePayeePayorsClient } from 'app/api/apiClient/generated/corePayeePayorsClient';
import { publicPayeePayorsClient } from 'app/api/apiClient/generated/publicPayeePayorsClient';
import {
  PublicPayeePayorsSearchCompaniesParams,
  PublicPayeePayorsSearchNamesParams,
} from 'app/api/apiClient/generated/types';
import { useIsPayee } from 'app/api/hooks/core/v1/auth/user';
import { queries } from 'app/constants/queries';
import { useQueryEnhanced } from 'app/hooks/useQueryEnhanced';

interface UseGetPayorNamesParams extends Omit<Partial<PublicPayeePayorsSearchNamesParams>, 'hasPaperwork' | 'q'> {
  hasPaperwork: boolean;
  q: string;
}

export const useGetPayor = (id: string | undefined) => {
  const isPayee = useIsPayee();

  const fetcher = useCallback(() => corePayeePayorsClient.getCompany(id ?? ''),
    [id]);

  return useQueryEnhanced(
    [queries.payeePayorsShow, id],
    fetcher,
    {
      enabled: !!id && isPayee,
    },
  );
};

// eslint-disable-next-line @stylistic/max-len
export const useGetPayors = (queryParams?: Partial<PublicPayeePayorsSearchCompaniesParams>, options: { enabled: boolean } = { enabled: true }) => {
  const fetcher = useCallback(() => publicPayeePayorsClient.searchCompanies({
    endDate: undefined,
    exportReport: undefined,
    includeIcons: undefined,
    isInPaymentsNetwork: undefined,
    page: undefined,
    pageSize: undefined,
    payorTypeId: undefined,
    q: undefined,
    sessionCurrencyId: undefined,
    sortAsc: undefined,
    sortField: undefined,
    startDate: undefined,
    ...queryParams,
  }),
  [queryParams]);

  return useQueryEnhanced([queries.payeePayors, queryParams],
    fetcher,
    {
      enabled: options.enabled,
    });
};

export const useGetPayorsByName = (queryParams: UseGetPayorNamesParams) => {
  const fetcher = useCallback(() => publicPayeePayorsClient.searchNames({
    page: undefined,
    pageSize: undefined,
    sessionCurrencyId: undefined,
    sortAsc: undefined,
    sortField: undefined,
    ...queryParams,
  }),
  [queryParams]);

  return useQueryEnhanced(
    [queries.payeePayorsByName, queryParams],
    fetcher,
    {
      enabled: !!queryParams.q,
    },
  );
};

export const useGetPayorNames = (payorIds: string[]) => {
  const isPayee = useIsPayee();

  const queryFn = useCallback(() => corePayeePayorsClient.lookupNames({ ids: payorIds }),
    [payorIds]);

  const {
    data,
    isInitialLoading,
  } = useQueryEnhanced(
    [queries.payeePayorNames],
    queryFn,
    {
      enabled: isPayee && payorIds.length > 0,
    },
  );

  return useMemo(() => {
    const payorNames = data?.names ?? [];

    payorNames.sort((a, b) => (a.name ?? '') > (b.name ?? '') ? 1 : -1);

    return {
      data: payorNames,
      isInitialLoading,
    };
  },
  [data?.names, isInitialLoading]);
};
